import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import type { MeResponse } from '@shared/types.ts';
import { Icon } from './Icon.tsx';
import { useEventPolling } from '../lib/poll.ts';
import { isIOSSafari } from '../lib/install.ts';

export function AppShell({ me, children }: { me: MeResponse; children: React.ReactNode }) {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      document.documentElement.dataset.theme = mq.matches ? 'dark' : 'light';
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const membersById = useMemo(
    () => Object.fromEntries(me.members.map((m) => [m.id, { name: m.name, color: m.color }])),
    [me.members],
  );
  useEventPolling(me.active_baby_id, me.user.id, membersById);

  const [showInstall, setShowInstall] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('lt_install_dismissed') === '1') return;
    setShowInstall(isIOSSafari());
  }, []);

  return (
    <div className="lt-app">
      <div className="lt-viewport">{children}</div>
      {showInstall && (
        <Link
          to="/onboarding"
          style={{
            position: 'fixed',
            bottom: 'calc(var(--tab-h) + 12px + var(--safe-bottom))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
            maxWidth: 360,
            width: 'calc(100% - 32px)',
            padding: '10px 14px',
            borderRadius: 14,
            background: 'var(--accent)',
            color: 'var(--accent-ink)',
            fontSize: 13,
            fontWeight: 500,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <Icon.share s={16} />
          <span style={{ flex: 1 }}>Add Lion Tracker to your home screen</span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              localStorage.setItem('lt_install_dismissed', '1');
              setShowInstall(false);
            }}
            aria-label="Dismiss"
            style={{ color: 'var(--accent-ink)', opacity: 0.7 }}
          >
            <Icon.x s={14} />
          </button>
        </Link>
      )}
      <TabBar />
    </div>
  );
}

function TabBar() {
  const loc = useLocation();
  const path = loc.pathname;
  const active =
    path === '/' ? 'home'
    : path.startsWith('/chart') ? 'chart'
    : path.startsWith('/stats') ? 'stats'
    : 'more';

  const items: { id: string; label: string; to: string; icon: (p: { s: number; strokeWidth: number }) => React.ReactNode }[] = [
    { id: 'home', label: 'Today', to: '/', icon: (p) => <Icon.home {...p} /> },
    { id: 'chart', label: 'Chart', to: '/chart', icon: (p) => <Icon.chart {...p} /> },
    { id: 'stats', label: 'Stats', to: '/stats', icon: (p) => <Icon.stats {...p} /> },
    { id: 'more', label: 'More', to: '/more', icon: (p) => <Icon.more {...p} /> },
  ];

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50,
        width: '100%',
        maxWidth: 440,
        paddingBottom: 'var(--safe-bottom)',
        background:
          'linear-gradient(to top, var(--bg-app) 70%, color-mix(in oklab, var(--bg-app), transparent 30%))',
      }}
    >
      <div style={{ borderTop: '1px solid var(--border)' }} />
      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '8px 8px 6px' }}>
        {items.map((it) => {
          const on = it.id === active;
          return (
            <Link
              key={it.id}
              to={it.to}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                padding: '6px 14px',
                borderRadius: 12,
                color: on ? 'var(--accent)' : 'var(--text-3)',
                textDecoration: 'none',
              }}
            >
              {it.icon({ s: 24, strokeWidth: on ? 2.1 : 1.8 })}
              <div style={{ fontSize: 11, fontWeight: on ? 600 : 500, letterSpacing: -0.005 }}>{it.label}</div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
